import type { BannerData } from '@/interfaces/Home';
import type { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useColorMode, useToast } from '@eqi-investimentos/react';
import { Box, Flex, Spinner, Text, Button } from '@eqi-investimentos/react';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useBookmark } from '@/modules/Entertainment/http/client/mutations/useBookmark';
import { Icon } from '@/components/elements/Icon';
import { copyTextToClipboard } from '@/utils/clipboard';
import { IfRender } from '@/utils/jsx';
import { capitalizeFirstLetter } from '@/utils/string';
import { toastConfigDefault } from '@/utils/toast';

export const BannerTopHorizontal: FC<BannerTopHorizontalProps> = ({
  actions,
  bg_image,
  description,
  qtd_content,
  thumb,
  time_duration,
  typeScreen,
  refreshData,
}) => {
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';

  const { mutate: bookmark, isPending } = useBookmark(['bookmark-courses']);
  const { mutate: sendClickEvent } = useSendClickEvent();
  const toast = useToast(toastConfigDefault.primary);

  const contentDescription = /^\d+$/.test(qtd_content)
    ? `${qtd_content} episódios`
    : qtd_content;

  type ActionType = 'bookmark' | 'share';

  const actionsBytype: Record<
    ActionType,
    (action: BannerData['actions'][number]['action']) => void
  > = {
    bookmark: (action) => {
      bookmark(
        {
          id: action?.id ?? '',
          bookmarked: action?.currentBookmarked ?? false,
          object: 'program',
        },
        { onSuccess: refreshData }
      );
    },
    share: (action) => {
      copyTextToClipboard(action?.href);
      toast({
        description: 'Link copiado com sucesso!',
        status: 'warning',
      });
    },
  };

  const handleAction = (action: BannerData['actions'][number]['action']) => {
    if (action && (action.type === 'bookmark' || action.type === 'share')) {
      actionsBytype[action.type](action);
    }
  };

  const handleSendClick = (values: { label: string; url: string }) => {
    const label = typeScreen === 'entertainment' ? 'Play' : 'Começar o curso';

    sendClickEvent({
      label:
        salesforceOpportunityConfig.sendClickEvent.bannerCarrouselCourseMovies.label(
          label,
          values.url
        ),
    });
  };

  return (
    <Box
      pos="relative"
      w="full"
      h="full"
      bg={`url(${bg_image}) lightgray 50% / cover no-repeat`}
      transition="all 300ms ease-in-out"
      _after={{
        pos: 'absolute',
        content: "''",
        top: 0,
        left: 0,
        w: 'full',
        h: 'full',
        bgImage: isDarkMode
          ? 'linear-gradient(to right, alpha.50, alpha.150, transparent)'
          : 'linear-gradient(to right, alpha.950, alpha.400, transparent)',
        zIndex: -1,
      }}
      pb="6.25rem"
      pt="4.5rem"
      pl="5.75rem"
      zIndex={0}
    >
      <Flex
        flexDir="column"
        alignItems="flex-start"
        justifyContent="center"
        gap="s"
        maxW="36rem"
        h="full"
        zIndex={10}
        color="content.alwaysLight"
      >
        <Box position="relative" w="13rem" h="7.4rem">
          <Image
            className="banner-thumb"
            alt={description}
            src={thumb}
            fill
            style={{ objectFit: 'contain', borderRadius: 'xs' }}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/eqi-placeholder.png';
            }}
          />
        </Box>
        <Text size="xl">{description}</Text>
        <Text size="xl">
          {contentDescription} • {time_duration}
        </Text>
        <Flex alignItems="center" gap="m">
          {actions.map(
            (
              { href, title, icon, buttonStyle, action, hasSpinner, hidden },
              idx
            ) => {
              if (hidden) return;
              if (href) {
                return (
                  <Flex as={Link} key={idx} href={href}>
                    <Button
                      variant={'solid'}
                      py="m"
                      px={buttonStyle === 'naked' ? 'l' : 'xl'}
                      gap={buttonStyle === 'naked' ? '2xs' : 'xl'}
                      borderRadius="2xs"
                      color="content.alwaysLight"
                      bg="content.brand"
                      rightIcon={
                        <Icon
                          iconName={icon}
                          w={16}
                          h={16}
                          color="content.alwaysLight"
                        />
                      }
                      onClick={() =>
                        handleSendClick({
                          label: title,
                          url: href ?? '',
                        })
                      }
                    >
                      <Text size="label.l" fontWeight="semibold">
                        {capitalizeFirstLetter(title)}
                      </Text>
                    </Button>
                  </Flex>
                );
              }

              return (
                <Button
                  key={idx}
                  variant={'solid'}
                  py="m"
                  px={buttonStyle === 'naked' ? 'l' : 'xl'}
                  gap={buttonStyle === 'naked' ? '2xs' : 'xl'}
                  borderRadius="2xs"
                  color="content.alwaysLight"
                  bg="content.brand"
                  rightIcon={
                    <Icon
                      iconName={icon}
                      w={16}
                      h={16}
                      color="content.alwaysLight"
                    />
                  }
                  onClick={() => handleAction(action)}
                >
                  <IfRender condition={isPending && !!hasSpinner}>
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      w={16}
                      h={16}
                      sx={{
                        '.chakra-spinner': {
                          w: 16,
                          h: 16,
                          color: 'content.alwaysLight',
                        },
                      }}
                    >
                      <Spinner />
                    </Flex>
                  </IfRender>
                  <IfRender condition={!isPending || !hasSpinner}>
                    <Text size="label.l" fontWeight="semibold">
                      {title}
                    </Text>
                  </IfRender>
                </Button>
              );
            }
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

type BannerTopHorizontalProps = {
  actions: BannerData['actions'];
  bg_image: string;
  description: string;
  qtd_content: string;
  thumb: string;
  time_duration: string;
  typeScreen?: string;
  refreshData: () => void;
};
